'use client';

import { useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { motion } from 'motion/react';

const videoCards = [
  {
    title: 'VideoMule Demo',
    thumbnail: '/assets/homepage/thumbnails/VideoMule Demo.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/Videomule%20demo%20for%20client.mp4'
  },
  {
    title: 'Tutorial Video',
    thumbnail: '/assets/homepage/thumbnails/Slack.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/updated-videos/how%20to%20create%20a%20channel%20in%20slack.mp4'
  },
  {
    title: 'How-To Guide',
    thumbnail: '/assets/homepage/thumbnails/calendar.webp',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/updated-videos/Create%20a%20Meeting%20in%20Google%20Calendar.mp4'
  },
  {
    title: 'Product Explanation Video',
    thumbnail: '/assets/homepage/thumbnails/Davinci.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/How%20To%20ADD%20TRANSITIONS%20In%20Davinci%20Resolve%2019%20Tutorial.mp4'
  },
  {
    title: 'SaaS Walkthrough',
    thumbnail: '/assets/homepage/thumbnails/Claude.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/claude%20%2B%20canva%20design%20anything%20in%20minutes%20(1).mp4'
  },
  {
    title: 'Educational Lesson',
    thumbnail: '/assets/homepage/thumbnails/Hubspot.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/updated-videos/Hubspot.mp4'
  },
  {
    title: 'Features Learning',
    thumbnail: '/assets/homepage/thumbnails/Photoshop.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/How%20to%20Remove%20any%20background%20in%20just%20one%20click.mp4'
  },
  {
    title: 'Course Video',
    thumbnail: '/assets/homepage/thumbnails/Notion AI.png',
    video:
      'https://gw-ai-social-media.b-cdn.net/VideoMule/VM-Homepage/updated-videos/How%20to%20Create%20a%20Daily%20Task%20Board%20in%20Notion%20(4).mp4'
  }
];

function VideoCard({
  title,
  thumbnail,
  video
}: {
  title: string;
  thumbnail: string;
  video: string;
}) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (playing) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setPlaying(!playing);
  };

  return (
    <div className="shrink-0 flex flex-col items-center gap-2.5 w-[320px] sm:w-[480px] md:w-[640px]">
      <div className="relative w-full overflow-hidden transition-all duration-300 border shadow-sm cursor-pointer group rounded-3xl border-border bg-card hover:shadow-lg aspect-video">
        {!playing && (
          <img
            src={thumbnail}
            alt={title}
            className="absolute inset-0 z-10 object-cover w-full h-full"
          />
        )}

        <video
          ref={videoRef}
          src={video}
          className="object-cover w-full h-full"
          controls={playing}
          playsInline
          loop
          preload="none"
          onEnded={() => setPlaying(false)}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
        />

        {!playing && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center"
            onClick={toggle}
          >
            <div className="flex items-center justify-center w-16 h-16 transition-all duration-300 rounded-full shadow-xl bg-card text-primary group-hover:scale-110">
              <Play
                className="ml-1 h-7 w-7"
                fill="currentColor"
              />
            </div>
          </div>
        )}
      </div>
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
    </div>
  );
}

export default function SampleVideos() {
  const row1 = [...videoCards, ...videoCards];

  return (
    <motion.section
      id="examples"
      className="overflow-hidden pt-14 md:pt-20 pb-14 md:pb-24"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="px-4 mx-auto max-w-7xl md:px-8">
        <div className="mb-8 text-center sm:mb-16">
          <span className="inline-flex items-center px-3 py-1 mb-4 text-xs font-medium border rounded-full border-primary/20 text-primary bg-videomule-light-green dark:bg-primary/10">
            Real results
          </span>
          <h2 className="text-2xl font-bold sm:text-3xl md:text-5xl text-foreground">
            Real video docs made with{' '}
            <span className="text-primary">VideoMule</span>
          </h2>
          <p className="max-w-xl mx-auto mt-3 text-sm text-muted-foreground sm:text-base md:text-lg">
            Every one of these was scripted, voiced, and rendered by VideoMule -
            no manual editing.
          </p>
        </div>
      </div>

      <div className="marquee-row">
        <div
          className="flex animate-marquee-left gap-[36px]"
          style={{ width: 'max-content' }}
        >
          {row1.map((card, i) => (
            <VideoCard
              key={`r1-${i}`}
              {...card}
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
}
